import React, { useEffect, useState, memo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import moment from "moment";
import { Col, Row } from "reactstrap";
import countries from "../../Country/country.json";
import { Dropdown } from "primereact/dropdown";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

function ForexChart({ formState, setFormState }) {
  const [activeIndex, setActiveIndex] = useState(2);
  const [chartLabels, setChartLabels] = useState([]);
  const [chartData1, setChartData1] = useState([]);
  const [chartData2, setChartData2] = useState([]);
  const [latestRate, setLatestRate] = useState(null);

  const [amountFrom, setAmountFrom] = useState("1.00");
  const [amountTo, setAmountTo] = useState("");
  const [lastEdited, setLastEdited] = useState("from");

  const { t } = useTranslation();

  const valueTemplate = (option, props) => {
    if (!option || !option.flag) return props.placeholder;
    return (
      <div className="p-dropdown-value">
        <img
          alt={option.name}
          src={option.flag}
          width="20"
          style={{ verticalAlign: "middle", marginRight: "8px" }}
        />
        <span>{t(`${option.currencyCode}.currency.label`)}</span>
      </div>
    );
  };

  const itemTemplate = (option) => {
    return (
      <div className="p-dropdown-item">
        <img
          alt={option.name}
          src={option.flag}
          width="20"
          style={{ verticalAlign: "middle", marginRight: "8px" }}
        />
        <span>{t(`${option.currencyCode}.currency.label`)}</span>
      </div>
    );
  };

  const swapCurrencies = () => {
    setFormState((prev) => ({
      ...prev,
      buyCurrency: prev.sellCurrency,
      sellCurrency: prev.buyCurrency,
    }));
  };

  const pair = `${formState.buyCurrency}${formState.sellCurrency}`;

  const chartLinelItem = [
    { label: t("year.label"), val: "year", format: "YYYY" },
    { label: t("month.label"), val: "month", format: "YYYY-MM" },
    { label: t("day.label"), val: "day", format: "YYYY-MM-DD" },
    { label: t("hour.label"), val: "hour", format: "HH:mm" },
  ];

  const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const getTimeRange = (index) => {
    const end = new Date();
    let start = new Date(end);

    switch (index) {
      case 0:
        start.setFullYear(end.getFullYear() - 10);
        break;
      case 1:
        start.setFullYear(end.getFullYear() - 1);
        break;
      case 2:
        start.setDate(end.getDate() - 30);
        break;
      case 3:
        start.setDate(end.getDate() - 3);
        break;
      default:
        break;
    }

    return {
      start: formatDate(start),
      end: formatDate(end),
    };
  };

  const fetchData = async () => {
    try {
      const { start, end } = getTimeRange(activeIndex);
      const interval = chartLinelItem[activeIndex].val;

      const response = await fetch(
        "https://aro3jn3404.execute-api.us-east-1.amazonaws.com/default/polygonFetcher",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pair, start, end, interval }),
        }
      );
      if (!response.ok) throw new Error("Network response was not ok");
      const jsonData = await response.json();

      if (jsonData.results) {
        let results = jsonData.results;
        if (activeIndex === 3) {
          const now = Date.now();
          const cutoff = now - 24 * 60 * 60 * 1000;
          results = results.filter((e) => e.t >= cutoff);
        }
        setChartLabels(
          results.map((e) =>
            moment(e.t).format(chartLinelItem[activeIndex].format)
          )
        );
        setChartData1(results.map((e) => e.c));
        setChartData2(results.map((e) => e.c + 0.031));
        const last = results[results.length - 1];
        setLatestRate(last ? last.c : null);
      } else {
        setChartData1([]);
        setChartData2([]);
        setLatestRate(null);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setLatestRate(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeIndex, pair]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: false },
    },
    scales: {
      x: {
        ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 },
        grid: { drawOnChartArea: false },
      },
      y: { beginAtZero: false },
    },
  };

  const sanitizeToTwoDecimals = (v) => {
    if (v === "") return "";
    let cleaned = v.replace(/[^\d.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot !== -1) {
      cleaned =
        cleaned.slice(0, firstDot + 1) +
        cleaned.slice(firstDot + 1).replace(/\./g, "");
    }
    const m = cleaned.match(/^(\d+)(?:\.(\d{0,2}))?$/);
    if (!m) {
      const parts = cleaned.split(".");
      if (parts.length === 1) return parts[0];
      return parts[0] + "." + parts[1].slice(0, 2);
    }
    return m[1] + (m[2] !== undefined ? "." + m[2] : "");
  };

  const fmt2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : "");

  useEffect(() => {
    if (!latestRate) {
      setAmountTo("");
      return;
    }
    if (lastEdited !== "from") return;
    const base = amountFrom === "" ? 0 : Number(amountFrom);
    const val = base * Number(latestRate);
    setAmountTo(fmt2(val));
  }, [amountFrom, latestRate, lastEdited]);

  useEffect(() => {
    if (!latestRate) return;
    if (lastEdited !== "to") return;
    const quote = amountTo === "" ? 0 : Number(amountTo);
    const val = quote / Number(latestRate);
    setAmountFrom(fmt2(val));
  }, [amountTo, latestRate, lastEdited]);

  const onChangeFrom = (e) => {
    setLastEdited("from");
    setAmountFrom(sanitizeToTwoDecimals(e.target.value));
  };

  const onChangeTo = (e) => {
    setLastEdited("to");
    setAmountTo(sanitizeToTwoDecimals(e.target.value));
  };

  return (
    <div
      style={{
        backgroundColor: "#e9ecef",
        padding: "1rem",
        borderRadius: "8px",
        height: "100%",
        position: "relative",
      }}
    >
      <br />

      {/* 居中且限制整体宽度 */}
      <div className="mx-auto" style={{ maxWidth: 1000 }}>
        <Row
          className="justify-content-center align-items-center g-4"
          style={{ minHeight: 110 }}
        >
          {/* 左侧 */}
          <Col xs="12" md="auto">
            <div style={{ width: "clamp(240px, 24vw, 320px)" }}>
              <div className="form-group mb-2">
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control"
                  placeholder={t("amount.label") || "Amount"}
                  value={amountFrom}
                  onChange={onChangeFrom}
                  onFocus={() => setLastEdited("from")}
                />
              </div>
              <div className="form-group mb-0">
                <Dropdown
                  id="buyCurrency"
                  name="buyCurrency"
                  value={formState.buyCurrency}
                  options={countries}
                  onChange={(e) =>
                    setFormState({ ...formState, buyCurrency: e.value })
                  }
                  optionLabel="currencyName"
                  optionValue="currencyCode"
                  className="w-100"
                  filter
                  filterBy="currencyName"
                  placeholder={t("select.label")}
                  valueTemplate={valueTemplate}
                  itemTemplate={itemTemplate}
                />
              </div>
            </div>
          </Col>

          {/* 中间箭头 */}
          <Col xs="auto" className="p-0">
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ width: 64, height: 110 }}
            >
              <i
                className="pi pi-arrow-right-arrow-left"
                style={{ fontSize: "1.6rem", lineHeight: 1, cursor: "pointer" }}
                onClick={swapCurrencies}
                title={t("swap.label") || "Swap"}
              />
            </div>
          </Col>

          {/* 右侧 */}
          <Col xs="12" md="auto">
            <div style={{ width: "clamp(240px, 24vw, 320px)" }}>
              <div className="form-group mb-2">
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control"
                  placeholder={t("amount.label") || "Amount"}
                  value={amountTo}
                  onChange={onChangeTo}
                  onFocus={() => setLastEdited("to")}
                />
              </div>
              <div className="form-group mb-0">
                <Dropdown
                  id="sellCurrency"
                  name="sellCurrency"
                  value={formState.sellCurrency}
                  options={countries}
                  onChange={(e) =>
                    setFormState({ ...formState, sellCurrency: e.value })
                  }
                  optionLabel="currencyName"
                  optionValue="currencyCode"
                  className="w-100"
                  filter
                  filterBy="currencyName"
                  placeholder={t("select.label")}
                  valueTemplate={valueTemplate}
                  itemTemplate={itemTemplate}
                />
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* 灰字靠右 */}
      <div className="d-flex justify-content-end align-items-center mb-2">
        <strong className="text-muted text-end">
          {latestRate
            ? `${t("rate.now") || "Current rate"}: 1 ${formState.buyCurrency} ≈ ${latestRate} ${formState.sellCurrency}`
            : `${t("rate.loading") || "Loading rate..."}`}
        </strong>
      </div>

      <div className="d-flex gap-2 justify-content-end mb-2">
        {chartLinelItem.map((item, i) => (
          <button
            key={item.label}
            className={`btn btn-sm btn-outline-primary ${
              i === activeIndex ? "active" : ""
            }`}
            onClick={() => setActiveIndex(i)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {chartData1 && chartData1.length > 0 ? (
        <div
          style={{
            backgroundColor: "#e9ecef",
            padding: "1rem",
            borderRadius: "8px",
            height: "70%",
            position: "relative",
          }}
        >
          <Line
            data={{
              labels: chartLabels,
              datasets: [
                {
                  label: "Polygon Rate",
                  data: chartData1,
                  borderColor: "rgb(235, 53, 95)",
                  backgroundColor: "rgba(235, 53, 95, 0.2)",
                  fill: true,
                },
                {
                  label: "Miracle Rate",
                  data: chartData2,
                  borderColor: "rgb(53, 162, 235)",
                  backgroundColor: "rgba(53, 162, 235, 0.2)",
                  fill: true,
                },
              ],
            }}
            options={options}
          />
        </div>
      ) : (
        <p className="text-muted text-center mt-5">No data available.</p>
      )}
    </div>
  );
}

export default memo(ForexChart);
